/**
 * Enhanced date utilities for user-specific timezone and language formatting
 * Handles date/datetime formatting based on user's language and timezone preferences
 */

/**
 * Formats a datetime string to user's locale and timezone.
 * Handles ISO 8601 format (e.g., "2025-07-24T06:48:21.000Z") and "YYYY-MM-DD HH:mm:ss".
 * 
 * @param {string|Date} datetimeString - DateTime string or Date object.
 * @param {string} userLanguage - User's language code (e.g., "de", "en").
 * @param {string} userTimezone - User's timezone (e.g., "Europe/Berlin").
 * @returns {string} Formatted datetime string.
 */
export function formatDateTimeForUser(datetimeString, userLanguage, userTimezone) {
    if (!datetimeString) {
        return '';
    }

    try {
        let date;
        if (typeof datetimeString === 'string' && datetimeString.includes(' ') && !datetimeString.includes('T')) {
             // Assuming "YYYY-MM-DD HH:mm:ss" format, treat as UTC
             date = new Date(datetimeString.replace(' ', 'T') + 'Z');
        } else {
             // Handles ISO strings (e.g., "2025-10-04T22:00:00.000Z") and Date objects
             date = new Date(datetimeString);
        }

        if (isNaN(date.getTime())) {
            console.error('Invalid datetime string:', datetimeString);
            return datetimeString;
        }

        // Define locale-specific formatting options
        const formatOptions = getDateTimeFormatOptions(userLanguage);
        
        // Create formatter with user's locale and timezone
        const formatter = new Intl.DateTimeFormat(getLocaleString(userLanguage), {
            ...formatOptions,
            timeZone: userTimezone
        });

        return formatter.format(date);
        
    } catch (error) {
        console.error('Error formatting datetime for user:', error);
        return datetimeString;
    }
}

/**
 * Formats a datetime string to user's date format only (no time).
 * Handles ISO 8601 format (e.g., "2025-07-24T06:48:21.000Z") and "YYYY-MM-DD HH:mm:ss".
 * 
 * @param {string|Date} datetimeString - DateTime string or Date object.
 * @param {string} userLanguage - User's language code (e.g., "de", "en").
 * @param {string} userTimezone - User's timezone (e.g., "Europe/Berlin").
 * @returns {string} Formatted date string (no time).
 */
export function formatDateOnlyForUser(datetimeString, userLanguage, userTimezone) {
    if (!datetimeString) {
        return '';
    }

    try {
        let date;
        if (typeof datetimeString === 'string' && datetimeString.includes(' ') && !datetimeString.includes('T')) {
             // Assuming "YYYY-MM-DD HH:mm:ss" format, treat as UTC
             date = new Date(datetimeString.replace(' ', 'T') + 'Z');
        } else {
             // Handles ISO strings (e.g., "2025-10-04T22:00:00.000Z") and Date objects
             date = new Date(datetimeString);
        }

        if (isNaN(date.getTime())) {
            console.error('Invalid datetime string:', datetimeString);
            return datetimeString;
        }

        // Define locale-specific date-only formatting options
        const formatOptions = getDateOnlyFormatOptions(userLanguage);
        
        // Create formatter with user's locale and timezone
        const formatter = new Intl.DateTimeFormat(getLocaleString(userLanguage), {
            ...formatOptions,
            timeZone: userTimezone
        });

        return formatter.format(date);
        
    } catch (error) {
        console.error('Error formatting date for user:', error);
        return datetimeString;
    }
}

/**
 * Formats a date string to user's date format
 * Converts from format "2025-07-20" to user's preferred date format
 * 
 * @param {string} dateString - Date in format "YYYY-MM-DD"
 * @param {string} userLanguage - User's language code (e.g., "de", "en")
 * @param {string} userTimezone - User's timezone (e.g., "Europe/Berlin")
 * @returns {string} Formatted date string
 */
export function formatDateForUser(dateString, userLanguage, userTimezone) {
    if (!dateString) {
        return '';
    }

    try {
        // Parse the date string (assuming it's in UTC/neutral timezone)
        const date = new Date(dateString + 'T00:00:00Z');
        
        if (isNaN(date.getTime())) {
            console.error('Invalid date string:', dateString);
            return dateString;
        }

        // Define locale-specific date formatting options
        const formatOptions = getDateOnlyFormatOptions(userLanguage);
        
        // Create formatter with user's locale and timezone
        const formatter = new Intl.DateTimeFormat(getLocaleString(userLanguage), {
            ...formatOptions,
            timeZone: userTimezone
        });

        return formatter.format(date);
        
    } catch (error) {
        console.error('Error formatting date for user:', error);
        return dateString;
    }
}

/**
 * Get locale string based on user language
 * @param {string} userLanguage - User's language code
 * @returns {string} Locale string for Intl.DateTimeFormat
 */
function getLocaleString(userLanguage) {
    const localeMap = {
        'cs': 'cs-CZ',    // Czech
        'da': 'da-DK',    // Danish
        'nl': 'nl-NL',    // Dutch
        'en': 'en-GB',    // English (GB)
        'fr': 'fr-FR',    // French
        'de': 'de-DE',    // German
        'it': 'it-IT',    // Italian
        'pl': 'pl-PL',    // Polish
        'pt': 'pt-PT',    // Portuguese
        'es': 'es-ES'     // Spanish
    };
    
    return localeMap[userLanguage] || 'en-GB';
}

/**
 * Get date and time formatting options based on user language
 * @param {string} userLanguage - User's language code
 * @returns {object} Formatting options for Intl.DateTimeFormat
 */
function getDateTimeFormatOptions(userLanguage) {
    const baseOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false // Use 24-hour format
    };

    // Language-specific customizations
    switch (userLanguage) {
        case 'de':
        case 'cs':
        case 'da':
        case 'pl':
            return {
                ...baseOptions,
                // German/Czech/Danish/Polish format: DD.MM.YYYY HH:mm
            };
        case 'en':
            return {
                ...baseOptions,
                // English (GB) format: DD/MM/YYYY HH:mm
            };
        case 'nl':
        case 'fr':
        case 'it':
        case 'pt':
        case 'es':
            return {
                ...baseOptions,
                // Dutch/French/Italian/Portuguese/Spanish format follows local conventions
            };
        default:
            return baseOptions;
    }
}

/**
 * Get date-only formatting options based on user language
 * @param {string} userLanguage - User's language code
 * @returns {object} Formatting options for Intl.DateTimeFormat
 */
function getDateOnlyFormatOptions(userLanguage) {
    const baseOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    };

    // Language-specific customizations
    switch (userLanguage) {
        case 'de':
        case 'cs':
        case 'da':
        case 'pl':
            return {
                ...baseOptions,
                // German/Czech/Danish/Polish format: DD.MM.YYYY
            };
        case 'en':
            return {
                ...baseOptions,
                // English (GB) format: DD/MM/YYYY
            };
        case 'nl':
        case 'fr':
        case 'it':
        case 'pt':
        case 'es':
            return {
                ...baseOptions,
                // Dutch/French/Italian/Portuguese/Spanish format follows local conventions
            };
        default:
            return baseOptions;
    }
}

/**
 * Utility function to format multiple date fields for a user
 * Useful for processing objects with multiple date/datetime fields
 * 
 * @param {object} data - Object containing date fields
 * @param {array} dateFields - Array of field names that contain dates
 * @param {array} datetimeFields - Array of field names that contain datetimes
 * @param {string} userLanguage - User's language code
 * @param {string} userTimezone - User's timezone
 * @returns {object} Object with formatted date fields
 */
export function formatMultipleDatesForUser(data, dateFields = [], datetimeFields = [], userLanguage, userTimezone) {
    if (!data || (!dateFields.length && !datetimeFields.length)) {
        return data;
    }

    const formattedData = { ...data };

    // Format date fields
    dateFields.forEach(field => {
        if (formattedData[field]) {
            formattedData[field] = formatDateForUser(formattedData[field], userLanguage, userTimezone);
        }
    });

    // Format datetime fields
    datetimeFields.forEach(field => {
        if (formattedData[field]) {
            formattedData[field] = formatDateTimeForUser(formattedData[field], userLanguage, userTimezone);
        }
    });

    return formattedData;
}

/**
 * Helper function to get current date/time in user's timezone and format
 * @param {string} userLanguage - User's language code
 * @param {string} userTimezone - User's timezone
 * @param {boolean} includeTime - Whether to include time (default: true)
 * @returns {string} Current date/time formatted for user
 */
export function getCurrentDateTimeForUser(userLanguage, userTimezone, includeTime = true) {
    const now = new Date();
    
    if (includeTime) {
        const formatOptions = getDateTimeFormatOptions(userLanguage);
        const formatter = new Intl.DateTimeFormat(getLocaleString(userLanguage), {
            ...formatOptions,
            timeZone: userTimezone
        });
        return formatter.format(now);
    } else {
        const formatOptions = getDateOnlyFormatOptions(userLanguage);
        const formatter = new Intl.DateTimeFormat(getLocaleString(userLanguage), {
            ...formatOptions,
            timeZone: userTimezone
        });
        return formatter.format(now);
    }
}

/**
 * Example usage function showing how to use these utilities with user data
 * @param {object} user - User object containing language and timezone
 * @param {object} data - Data object with date fields to format
 * @returns {object} Formatted data
 */
export function formatUserSpecificDates(user, data) {
    const { language, timezone } = user;
    
    // Example for the data structure you provided
    const formattedData = {
        ...data,
        createDate: formatDateTimeForUser(data.createDate, language, timezone),
        invoiceDateFrom: formatDateForUser(data.invoiceDateFrom, language, timezone),
        invoiceDateTo: formatDateForUser(data.invoiceDateTo, language, timezone)
    };
    
    return formattedData;
}